import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { CategoryStat } from '../api/types';
import { count, percent } from '../utils/format';

import { AxisTick } from './AxisTick';
import { ChartTooltip } from './ChartTooltip';
import { CHROME, gapColor } from './palette';

/**
 * Ranked magnitude with a signed zero: horizontal bars so the category names
 * stay readable, a reference line at 0, and a diverging fill because sitting
 * below the market low means the opposite of sitting above it.
 */
/** ~6px per character at 11px, so this many characters fit the axis width. */
const AXIS_WIDTH = 172;
const MAX_LABEL = 26;

export function CategoryGapChart({ stats }: { stats: CategoryStat[] }) {
  const rows = stats
    .filter((stat) => stat.avgGapPct !== null)
    .map((stat) => ({ ...stat, avgGapPct: Number(stat.avgGapPct) }))
    .sort((left, right) => right.avgGapPct - left.avgGapPct)
    .slice(0, 12);

  if (rows.length === 0) {
    return <div className="state">No category data yet — scan some products first.</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={Math.max(200, rows.length * 32 + 40)}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 40, bottom: 4, left: 4 }} barCategoryGap={4}>
        <CartesianGrid stroke={CHROME.grid} horizontal={false} />
        <XAxis
          type="number"
          tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(value: number) => `${value}%`}
        />
        <YAxis
          type="category"
          dataKey="category"
          axisLine={false}
          tickLine={false}
          width={AXIS_WIDTH}
          /* One line per tick, always. The full path is in the tooltip. */
          tick={(props) => <AxisTick {...props} max={MAX_LABEL} />}
        />
        <ReferenceLine x={0} stroke={CHROME.inkMuted} strokeWidth={1} />
        <Tooltip
          cursor={{ fill: 'rgba(255,255,255,0.05)' }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) {
              return null;
            }

            const row = payload[0].payload as CategoryStat & { avgGapPct: number };

            return (
              <ChartTooltip
                title={row.category}
                note={row.categoryPath ?? undefined}
                rows={[
                  {
                    key: 'gap',
                    label: 'Mean gap',
                    value: percent(row.avgGapPct),
                    color: gapColor(row.avgGapPct),
                  },
                  { key: 'products', label: 'Products', value: count(row.products) },
                  { key: 'cheapest', label: 'We are cheapest', value: count(row.cheapestCount) },
                  {
                    key: 'offers',
                    label: 'Mean offers',
                    value: row.avgOfferCount === null ? '—' : Number(row.avgOfferCount).toFixed(1),
                  },
                ]}
              />
            );
          }}
        />
        <Bar isAnimationActive={false} dataKey="avgGapPct" radius={4} maxBarSize={22}>
          {rows.map((row) => (
            <Cell key={row.category} fill={gapColor(row.avgGapPct)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
